import { type PropsWithChildren } from 'react';

import { useGridPlotSize } from '../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { defaultUnitFormatter } from '../../core/utils/defaultUnitFormatter.js';
import { formatDateBasedOnDomain } from '../../core/utils/format-date-based-on-domain.js';
import {
  getFormattedTicks,
  getFormatterByTicks,
} from '../../core/utils/formatter-by-ticks.js';
import {
  getHorizontalNumTicks,
  getVerticalNumTicks,
} from '../../core/utils/ticks.js';
import { TimeseriesTickAxisContext } from '../context/TimeseriesTickAxis.context.js';
import { TimeseriesXFormatterContext } from '../context/TimeseriesXFormatter.context.js';
import { TimeseriesYFormatterContext } from '../context/TimeseriesYFormatter.context.js';
import { useTimeseriesFilteredYAxis } from '../hooks/useTimeseriesFilteredYAxis.js';
import { useTimeseriesUnits } from '../hooks/useTimeseriesUnits.js';
import { useValueRepresentation } from '../hooks/useValueRepresentation.js';
import { useXScales } from '../hooks/useXScale.js';
import { useYScales } from '../hooks/useYScales.js';

export const TimeseriesFormatterProvider = ({
  children,
}: PropsWithChildren) => {
  const { height, width } = useGridPlotSize();
  const { left, right } = useYScales();
  const { currentScale } = useXScales();
  const yAxes = useTimeseriesFilteredYAxis();
  const [leftUnit, rightUnit] = useTimeseriesUnits();
  const valueRepresentation = useValueRepresentation();
  const isRelative =
    valueRepresentation === 'relative' && leftUnit === 'percent';

  const verticalNumTicks = getVerticalNumTicks(height);
  const horizontalNumTicks = getHorizontalNumTicks(width);

  const leftFormatter = isRelative
    ? defaultUnitFormatter(leftUnit)
    : getFormatterByTicks(
        verticalNumTicks,
        left?.scale,
        leftUnit,
        yAxes.left?.formatter,
      );

  const isOnlyRightAxisConfigured = !yAxes.left && yAxes.right && !rightUnit;
  const resolvedRightUnit = isOnlyRightAxisConfigured ? leftUnit : rightUnit;

  const rightFormatter = isRelative
    ? defaultUnitFormatter(resolvedRightUnit)
    : getFormatterByTicks(
        verticalNumTicks,
        right?.scale,
        resolvedRightUnit,
        yAxes.right?.formatter,
      );

  const leftTicks = getFormattedTicks(
    verticalNumTicks,
    left?.scale,
    leftUnit,
    leftFormatter,
  );

  const rightTicks = getFormattedTicks(
    verticalNumTicks,
    right?.scale,
    isOnlyRightAxisConfigured ? leftUnit : rightUnit,
    rightFormatter,
  );

  const bottomTicks = getFormattedTicks(
    horizontalNumTicks,
    currentScale,
    undefined,
    undefined,
  );

  const bottomFormatter = (time: Date) =>
    formatDateBasedOnDomain(time, currentScale.domain() as [Date, Date], width);

  return (
    <TimeseriesYFormatterContext.Provider
      value={[leftFormatter, rightFormatter]}
    >
      <TimeseriesXFormatterContext.Provider value={bottomFormatter}>
        <TimeseriesTickAxisContext.Provider
          value={{
            left: leftTicks,
            right: rightTicks,
            bottom: bottomTicks,
          }}
        >
          {children}
        </TimeseriesTickAxisContext.Provider>
      </TimeseriesXFormatterContext.Provider>
    </TimeseriesYFormatterContext.Provider>
  );
};
